import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Users,
  TrendingUp,
  DollarSign,
  Link2,
  Copy,
  ExternalLink,
  Gift,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Zap,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { PartnerPortalService, type PartnerDashboardData } from '@/domain/partner-portal'

export function AffiliateDashboard() {
  const [dashboardData, setDashboardData] = useState<PartnerDashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const fetchDashboard = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true)
    else setIsLoading(true)
    setError(null)

    try {
      const result = await PartnerPortalService.getDashboard()
      
      if (result.success && result.data) {
        setDashboardData(result.data)
      } else {
        setError(result.error || 'ไม่สามารถโหลดข้อมูลได้')
      }
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาด')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
  }, [])

  const copyLink = () => {
    const user = PartnerPortalService.getCurrentUser()
    if (user) {
      navigator.clipboard.writeText(`https://winner99.com/ref/${user.objectId.slice(-8).toUpperCase()}`)
    }
  }

  // Stats cards data from API
  const summary = dashboardData?.summary
  const statsCards = summary ? [
    {
      title: 'ยอดคอมมิชชั่นสะสม',
      value: formatCurrency(summary.total_earned || 0),
      subtitle: `คงเหลือ ${formatCurrency(summary.current_balance || 0)}`,
      icon: <DollarSign className="h-5 w-5" />,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      title: 'สมาชิกที่แนะนำ (L1)',
      value: (summary.l1_count || 0).toLocaleString(),
      subtitle: `รวม ${summary.total_referrals || 0} คน`,
      icon: <Users className="h-5 w-5" />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'สมาชิกชั้น 2 (L2)',
      value: (summary.l2_count || 0).toLocaleString(),
      subtitle: 'จากการแนะนำต่อ',
      icon: <TrendingUp className="h-5 w-5" />,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    {
      title: 'ถอนแล้ว',
      value: formatCurrency(summary.total_withdrawn || 0),
      subtitle: 'ยอดรวมทั้งหมด',
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
  ] : []

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="mt-4 space-y-2">
                  <Skeleton className="h-8 w-24" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-lg font-medium mb-2">ไม่สามารถโหลดข้อมูลได้</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => fetchDashboard()}>ลองใหม่</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">แดชบอร์ด</h1>
          <p className="text-sm text-muted-foreground">ภาพรวมรายได้และสมาชิกที่แนะนำ</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchDashboard(true)} disabled={isRefreshing}>
          <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
          รีเฟรช
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={cn("p-2.5 rounded-lg", stat.bgColor)}>
                  <div className={stat.color}>{stat.icon}</div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Real-time Today Card */}
      {dashboardData?.today_realtime && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    รายได้วันนี้ (Real-time)
                    {dashboardData.today_realtime.cached && (
                      <Badge variant="secondary" className="text-[10px]">
                        cached {dashboardData.today_realtime.cache_age_seconds}s ago
                      </Badge>
                    )}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    คำนวณจาก {dashboardData.today_realtime.members_calculated} สมาชิก
                    {dashboardData.today_realtime.optimization && (
                      <span className="ml-1">
                        (ข้าม {dashboardData.today_realtime.optimization.skipped} คนที่ไม่มีกิจกรรม)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(dashboardData.today_realtime.total)}
                </p>
                <div className="text-xs text-muted-foreground mt-1">
                  L1: {formatCurrency(dashboardData.today_realtime.l1_amount)} | L2: {formatCurrency(dashboardData.today_realtime.l2_amount)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions & Last 7 Days */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Referral Link */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              ลิงก์แนะนำของคุณ
            </CardTitle>
            <CardDescription>แชร์ลิงก์นี้เพื่อรับคอมมิชชั่น</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 p-3 rounded-lg bg-muted font-mono text-sm truncate">
                https://winner99.com/ref/{PartnerPortalService.getCurrentUser()?.objectId.slice(-8).toUpperCase() || 'XXXXXX'}
              </div>
              <Button variant="outline" size="icon" onClick={copyLink}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="gap-2">
                <Link2 className="h-4 w-4" />
                สร้างลิงก์ใหม่
              </Button>
              <Button variant="outline" className="gap-2">
                ดู QR Code
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Last 7 Days */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              รายได้ 7 วันล่าสุด
            </CardTitle>
            <CardDescription>รายได้แยกตามวัน</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData?.last_7_days && dashboardData.last_7_days.length > 0 ? (
              <div className="space-y-2">
                {dashboardData.last_7_days.map((day, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                    <span className="text-sm text-muted-foreground">{day.date}</span>
                    <div className="text-right">
                      <span className="font-medium">{formatCurrency(day.total)}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        (L1: {formatCurrency(day.l1_amount)}, L2: {formatCurrency(day.l2_amount)})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>ยังไม่มีข้อมูลรายได้</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Tips */}
      <Card className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-indigo-500/20">
              <Gift className="h-6 w-6 text-indigo-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold mb-1">เคล็ดลับเพิ่มรายได้</h3>
              <p className="text-sm text-muted-foreground mb-3">
                แชร์ลิงก์ในโซเชียลมีเดีย สร้างคอนเทนต์รีวิว หรือใช้ QR Code เพื่อเข้าถึงกลุ่มเป้าหมายได้ง่ายขึ้น
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary">
                  ดูเคล็ดลับเพิ่มเติม
                </Button>
                <Button size="sm" variant="outline">
                  ติดต่อผู้จัดการพันธมิตร
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}










